import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import {
  AdBlastUnitInput,
  type OneToOneAdSetType,
  useTriggerAdBlastMutation,
} from "~/ui-rtk/api/jobsApi"
import {
  useAdForgeGetDefaultsQuery,
  useAdForgeUpdateCreativeMutation,
} from "~/ui-rtk/api/adForgeApi"
import AdNameBuilder from "~/ui-rtk/pages/AdForge/components/AdNameBuilder"
import {
  useAnalyzeMediaMutation,
  useGetCreativeTestEligibilityQuery,
  useSearchAdSetsQuery,
  type AnalyzeMediaJobResult,
} from "~/ui-rtk/api/paidMediaApi"
import type {
  AdSet,
  CreativeTestEligibility,
  SupportedPlatform,
} from "~/ui-rtk/api/types/paid-media.types"
import { useResolveAssetsMutation } from "~/ui-rtk/api/assetsApi"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import { useWaitForJob } from "~/ui-rtk/hooks/useWaitForJob"
import { suggestionsToSelection } from "~/ui-rtk/pages/AdForge/components/tagSuggestions"
import {
  isVideoUrl,
  mapMediaChannelToSourceType,
} from "~/ui-rtk/components/atoms/MarathonImage"
import MarathonImage, {
  type AssetSourceType,
} from "~/ui-rtk/components/atoms/MarathonImage/MarathonImage"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import CheckboxWithLabel from "~/ui-rtk/components/ui/controls/CheckboxWithLabel"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select, {
  type TSelectOption,
} from "~/ui-rtk/components/ui/controls/Select"
import { useRequiredAdTags } from "~/ui-rtk/pages/AdForge/components/useRequiredAdTags"
import { useCheckServiceIsConnected } from "~/ui-rtk/hooks/connected-services"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import { brandRoutes } from "~/ui-rtk/constants/routes/brand-routes"
import { Link } from "react-router-dom"
import {
  isProfileVisitAdSet,
  isValidHttpUrl,
  normalizeUrl,
} from "~/ui-rtk/utils/ad-set-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { formatNumber } from "~/ui-rtk/utils/format"
import useDate from "~/ui-rtk/hooks/date"
import ScheduleBlastControls, {
  type BlastScheduleValue,
} from "./ScheduleBlastControls"
import {
  DEFAULT_ONE_TO_ONE,
  ONE_TO_ONE_ADSET_TYPE_OPTIONS,
  ONE_TO_ONE_GOAL_OPTIONS,
  isProfileVisitGoal,
  parseDailyBudget,
  type OneToOneAdSetState,
} from "./oneToOneAdSets"
import {
  CREATIVE_TEST_MAX_VARIANTS,
  CREATIVE_TEST_MIN_VARIANTS,
  CREATIVE_TEST_SUGGESTED_SHARE,
  DEFAULT_CREATIVE_TEST,
  creativeTestErrors,
  defaultStartDate,
  suggestedDailyBudgetDollars,
  toCreativeTestInput,
  type CreativeTestState,
} from "./creativeTest"

const dayjs = useDate()

/** Platforms the blast target picker can browse ad sets for. */
type BlastPlatform = Extract<SupportedPlatform, "meta" | "tiktok">

const PLATFORM_TAB_LABEL: Record<BlastPlatform, string> = {
  meta: "Meta",
  tiktok: "TikTok",
}

/** Friendly local-time rendering of the scheduled instant for the toast. */
function formatScheduledFor(iso: string): string {
  return dayjs(iso).format("MMM D, YYYY h:mm A")
}

/**
 * A row the user has picked from either the Creatives tab or the Ads tab.
 *
 * - `draft` → an `AdCreative` row saved by AdForge. Blast via
 *   `POST /ad-forge/creatives/:id/blast` which handles the strategy cascade
 *   (Rung 0 reuse across targets) server-side.
 * - `meta`  → an entry pulled live from the Meta `/adcreatives` library.
 *   No direct blast endpoint yet — we surface them in the picker, but mark
 *   them as skipped and require the user to import first. (Phase 6b will
 *   add a `POST /paid-media/meta/creatives/:id/blast` route.)
 * - `ad`    → an `Ad` row from the Ads tab. Blast via the existing
 *   `POST /paid-media/:platform/ads/:externalId/blast` which runs the full
 *   compatibility cascade.
 */
/**
 * Optional display-only fields the calling tab can supply so the modal can
 * render a preview of what's about to blast. None of these affect the API
 * call — they're purely informational so the user can spot-check the
 * selection before committing.
 */
export interface BlastSourcePreview {
  previewUrl?: string | null
  previewType?: "image" | "video" | null
  primaryText?: string | null
  headline?: string | null
  callToAction?: string | null
  /**
   * Asset-manager handle for the preview. When both fields are set the
   * modal renders the thumbnail via `<MarathonImage>` — same path AdDetails
   * uses — so the image resolves through `useResolveAssetsMutation` and the
   * `assetCacheSlice` Redux cache. Falls back to `previewUrl` for sources
   * that don't have an asset-manager entry (e.g. AdForge drafts).
   */
  assetSourceType?: AssetSourceType | null
  assetExternalRefId?: string | null
}

export type BlastSource = (
  | {
      kind: "draft"
      id: string
      name: string
      /**
       * The draft's tag selection (category slug → option ids), used to check
       * the company's required-tags rule before launching. Every category the
       * ad-name template references must have a value; the API enforces the
       * same rule in `prepareBlastAdCreative`, so blocking here just makes the
       * rejection visible before the user commits.
       */
      tags?: Record<string, string[]> | null
      /**
       * Whether the draft carries at least one video media asset. TikTok ads
       * are video-only, so a draft with `false` here blocks launch while
       * TikTok ad groups are selected (the API enforces the same rule in
       * `prepareBlastAdCreative`). `undefined` means the calling tab didn't
       * check — the server-side rejection still applies.
       */
      hasVideoAsset?: boolean
    }
  | { kind: "meta"; id: string; name: string }
  | {
      kind: "ad"
      id: string
      name: string
      platform: SupportedPlatform
      externalId: string
    }
  | {
      // Organic social post (IG/FB) sourced from Cube. Blast via
      // `POST /paid-media/:platform/posts/:postId/blast`, which clones the
      // post into a paid ad under each selected ad set.
      kind: "post"
      id: string
      name: string
      platform: SupportedPlatform
      postId: string
      /** Cube `media_channel` of the post — the API needs it to resolve bare Facebook post ids. */
      channel?: "facebook" | "instagram"
      /** Cached video URL of a video post (Cube `post_link`); the preview is then its thumbnail. */
      videoUrl?: string
    }
) &
  BlastSourcePreview

interface BlastTargetPickerModalProps {
  open: boolean
  onClose: () => void
  /** Pre-resolved rows from the calling tab. */
  sources: BlastSource[]
}

// Lookup table keeps the chip label nestable-ternary-free and one obvious
// place to add a new source kind later.
const KIND_LABEL: Record<BlastSource["kind"], string> = {
  draft: "AdForge draft",
  meta: "Meta library",
  ad: "Launched ad",
  post: "Social post",
}

// Extracted to avoid a nested-ternary lint warning in the chip body. Picks
// the right thumbnail strategy per source: asset-manager (preferred) >
// raw <img> from previewUrl > empty placeholder div.
function renderThumbnail(
  source: BlastSource,
  canUseAssetManager: boolean,
  showRawImage: boolean,
  onError: (next: boolean) => void,
) {
  if (canUseAssetManager) {
    return (
      <MarathonImage
        sourceType={source.assetSourceType as AssetSourceType}
        externalRefId={source.assetExternalRefId as string}
        url={source.previewUrl ?? undefined}
        useThumbnail
        alt=""
        className="h-full w-full object-cover"
      />
    )
  }
  if (showRawImage) {
    return (
      <img
        src={source.previewUrl ?? ""}
        alt=""
        className="h-full w-full object-cover"
        onError={() => onError(true)}
      />
    )
  }
  return null
}

interface SourceChipProps {
  source: BlastSource
}
function SourceChip({ source }: SourceChipProps) {
  const kindLabel =
    source.kind === "ad" ? `${source.platform} ad` : KIND_LABEL[source.kind]
  // Some preview URLs (e.g. Meta post_preview) 404 or block CORS outside
  // their original embed context. Track that locally so the chip falls back
  // to a placeholder square instead of a broken-image icon.
  const [imgFailed, setImgFailed] = useState(false)
  const canUseAssetManager =
    !!source.assetSourceType && !!source.assetExternalRefId
  const showRawImage =
    !canUseAssetManager &&
    source.previewUrl &&
    !imgFailed &&
    source.previewType !== "video"
  return (
    <div
      className="flex w-56 shrink-0 items-center gap-system-2xs rounded-md border border-basic-blue bg-basic-black p-system-2xs"
      title={source.name}
    >
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-basic-dark-blue">
        {renderThumbnail(
          source,
          canUseAssetManager,
          !!showRawImage,
          setImgFailed,
        )}
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-xs font-medium text-white">
          {source.name}
        </span>
        <span className="truncate text-[10px] uppercase tracking-wide text-gray-400">
          {kindLabel}
        </span>
      </div>
    </div>
  )
}

interface SourcesPreviewProps {
  sources: BlastSource[]
}
function SourcesPreview({ sources }: SourcesPreviewProps) {
  // Empty case shouldn't happen — modal callers always invoke with a non-empty
  // selection — but guard anyway so we don't render a dangling label.
  if (sources.length === 0) return null
  return (
    <div className="flex flex-col gap-system-2xs">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-300">
        Blasting {sources.length} creative{sources.length === 1 ? "" : "s"}
      </span>
      {/* Horizontal scroll keeps the strip compact regardless of how many
       * rows the user picked. Each chip is fixed-width so the strip doesn't
       * jump as users scroll laterally. */}
      <div className="flex max-w-full gap-system-2xs overflow-x-auto pb-system-2xs">
        {sources.map(s => (
          <SourceChip key={s.id} source={s} />
        ))}
      </div>
    </div>
  )
}

// The word a user must type to blast into ad sets that don't look like
// testing ad sets (MD-5412). Exact, case-sensitive — the friction is the point.
const NON_TEST_CONFIRM_WORD = "CONFIRM"

/**
 * What the picker remembers about a selected ad set. Captured at toggle time
 * (not looked up from the search results at launch) because `adSets` only
 * holds the current search's rows — a selected row drops out of it when the
 * user keeps typing.
 *
 * - `name`         → the MD-5412 non-testing gate needs it.
 * - `profileVisit` → social-post blasts need a destination URL for every
 *   target that is NOT a Profile Visit ad set (PV ad sets force the IG
 *   profile URL + VIEW_INSTAGRAM_PROFILE server-side and ignore any URL).
 */
interface SelectedAdSet {
  name: string
  profileVisit: boolean
}

interface SocialPostDestinationUrlSectionProps {
  value: string
  /**
   * Number of selected targets that need the URL: non-PV ad sets, or — in
   * 1:1 mode — the selected campaigns (every new ad set shares one goal).
   */
  nonPvCount: number
  /** 1:1 mode: the targets are new ad sets, one per selected campaign. */
  oneToOne: boolean
  postCount: number
  /** Show the error state — only after the user has touched the field. */
  showError: boolean
  disabled: boolean
  onChange: (next: string) => void
  onBlur: () => void
}

/**
 * Destination URL prompt for social-post blasts into non-Profile-Visit ad
 * sets. An organic IG/FB post carries no external link of its own, so Meta
 * rejects the ad on URL-required objectives (TRAFFIC / SALES / LEADS /
 * ENGAGEMENT with a website destination) with subcode 2061015 "link field is
 * required". Blasts run as background jobs, so that rejection would only
 * surface as a failed job row — ask up front instead. Prefilled with the
 * company's AdForge default URL when one is set; the value is sent as
 * `overrides.destinationUrl` on every post unit.
 */
function SocialPostDestinationUrlSection({
  value,
  nonPvCount,
  oneToOne,
  postCount,
  showError,
  disabled,
  onChange,
  onBlur,
}: SocialPostDestinationUrlSectionProps) {
  const invalid = !isValidHttpUrl(value)
  const needsUrlText = oneToOne
    ? `The ${nonPvCount === 1 ? "new 1:1 ad set isn't" : `${nonPvCount} new 1:1 ad sets aren't`}`
    : `${nonPvCount === 1 ? "One selected ad set isn't" : `${nonPvCount} selected ad sets aren't`}`
  return (
    <div className="flex flex-col gap-system-2xs rounded-md border border-system-yellow-600/50 bg-system-yellow-900/20 px-system-sm py-system-xs">
      <div className="flex gap-system-2xs">
        <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0 text-system-yellow-400" />
        <div className="flex flex-col gap-[2px] text-xs">
          <span className="font-medium text-system-yellow-300">
            Destination URL required for social posts
          </span>
          <span className="text-gray-300">
            {`${needsUrlText} a Profile Visit ad set, so the ${
              postCount === 1 ? "post needs" : `${postCount} posts need`
            } a website URL to send people to. Profile Visit ad sets ignore this and link to the Instagram profile.`}
          </span>
        </div>
      </div>
      <Input
        fullWidth
        type="url"
        aria-label="Destination URL for social posts"
        placeholder="https://shop.example.com/collection"
        value={value}
        isError={showError && invalid}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
      />
      {showError && invalid && (
        <span className="text-xs text-states-error">
          Enter a full website URL (for example https://shop.example.com).
        </span>
      )}
    </div>
  )
}

interface NonTestAdSetConfirmDialogProps {
  /** Names of the selected targets that don't contain "test"/"testing". */
  adSetNames: string[]
  /** What was selected: existing ad sets, or campaigns in 1:1 mode. */
  targetNoun: "campaign" | "ad set"
  onCancel: () => void
  onConfirm: () => void
}

/**
 * Type-to-confirm gate shown when a blast targets ad sets whose names don't
 * contain "test"/"testing" (MD-5412). Rendered INSIDE the picker Dialog —
 * same reasoning as the nested AdNameBuilder below: the fixed overlay shares
 * the dialog's stacking context so it paints above it. Mounted only while
 * open, so the typed text resets every time the gate appears.
 */
function NonTestAdSetConfirmDialog({
  adSetNames,
  targetNoun,
  onCancel,
  onConfirm,
}: NonTestAdSetConfirmDialogProps) {
  const [confirmText, setConfirmText] = useState("")
  const confirmed = confirmText.trim() === NON_TEST_CONFIRM_WORD
  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-system-md">
      <div className="flex w-full max-w-md flex-col gap-system-sm rounded-lg border border-system-yellow-600/50 bg-basic-dark-blue p-system-md text-left shadow-xl">
        <div className="flex items-start gap-system-2xs">
          <ExclamationTriangleIcon className="mt-px h-5 w-5 shrink-0 text-system-yellow-400" />
          <div className="flex flex-col gap-system-2xs text-sm text-gray-200">
            <span className="font-semibold text-system-yellow-300">
              Heads up: you&apos;re launching ads into a non-testing{" "}
              {targetNoun}.
            </span>
            <span>
              {adSetNames.length === 1
                ? `This ${targetNoun} doesn't`
                : `These ${adSetNames.length} ${targetNoun}s don't`}{" "}
              have &quot;test&quot; in the name:
            </span>
            <ul className="max-h-32 list-inside list-disc overflow-y-auto text-xs text-white">
              {adSetNames.map(name => (
                <li key={name} className="truncate" title={name}>
                  {name}
                </li>
              ))}
            </ul>
            <span>
              If this is desired, type &quot;{NON_TEST_CONFIRM_WORD}&quot; below
              — if not, back out and select testing {targetNoun}s for this
              launch.
            </span>
          </div>
        </div>
        <Input
          fullWidth
          aria-label={`Type ${NON_TEST_CONFIRM_WORD} to launch anyway`}
          placeholder={`Type ${NON_TEST_CONFIRM_WORD}`}
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
        />
        <div className="flex items-center justify-end gap-system-sm">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-gray-200 hover:text-white"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmed}
            className="rounded-md bg-system-yellow-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-yellow-500 disabled:opacity-50"
          >
            Launch anyway
          </button>
        </div>
      </div>
    </div>
  )
}

const ONE_TO_ONE_TYPE_SELECT_OPTIONS: TSelectOption[] =
  ONE_TO_ONE_ADSET_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }))
const ONE_TO_ONE_GOAL_SELECT_OPTIONS: TSelectOption[] = ONE_TO_ONE_GOAL_OPTIONS

interface OneToOneAdSetSectionProps {
  value: OneToOneAdSetState
  /** Show the budget error state — only after the user has touched the field. */
  showBudgetError: boolean
  disabled: boolean
  onChange: (next: OneToOneAdSetState) => void
  onBudgetBlur: () => void
}

/**
 * "Launch each ad into its own 1:1 ad set" (MD-5426). When enabled the tree
 * above switches to campaign selection and the job creates one new PAUSED
 * ad set per source × campaign, shaped by the three fields here (audience
 * preset, optimization goal, daily minimum budget). Attribution is fixed by
 * the backend, so it is shown as a static line rather than a control.
 */
function OneToOneAdSetSection({
  value,
  showBudgetError,
  disabled,
  onChange,
  onBudgetBlur,
}: OneToOneAdSetSectionProps) {
  const selectedType = ONE_TO_ONE_ADSET_TYPE_OPTIONS.find(
    option => option.value === value.adSetType,
  )
  const budgetInvalid = parseDailyBudget(value.dailyBudget) === null
  return (
    <div className="flex flex-col gap-system-2xs">
      <CheckboxWithLabel
        className="checkbox checkbox-sm"
        checked={value.enabled}
        disabled={disabled}
        onChange={e => onChange({ ...value, enabled: e.target.checked })}
      >
        <span className="text-sm font-medium text-white">
          Launch each ad into its own 1:1 ad set
        </span>
      </CheckboxWithLabel>
      <span className="text-xs text-gray-300">
        One new paused ad set per ad × campaign. Targeting, placements and pixel
        are copied from the campaign&apos;s newest ad set; audiences follow the
        ad set type.
      </span>
      {value.enabled && (
        <div className="flex flex-col gap-system-2xs pt-system-2xs">
          <div className="grid grid-cols-1 gap-system-sm sm:grid-cols-3">
            <label className="flex flex-col gap-[2px] text-xs text-gray-300">
              Ad set type
              <Select
                aria-label="Ad set type"
                options={ONE_TO_ONE_TYPE_SELECT_OPTIONS}
                value={value.adSetType}
                disabled={disabled}
                onChange={e =>
                  onChange({
                    ...value,
                    adSetType: e.target.value as OneToOneAdSetType,
                  })
                }
              />
              {selectedType && (
                <span className="text-[11px] text-gray-400">
                  {selectedType.description}
                </span>
              )}
            </label>
            <label className="flex flex-col gap-[2px] text-xs text-gray-300">
              Optimization goal
              <Select
                aria-label="Optimization goal"
                options={ONE_TO_ONE_GOAL_SELECT_OPTIONS}
                value={value.optimizationGoal}
                disabled={disabled}
                onChange={e =>
                  onChange({ ...value, optimizationGoal: e.target.value })
                }
              />
            </label>
            <div className="flex flex-col gap-[2px] text-xs text-gray-300">
              Daily min. budget (USD)
              <Input
                fullWidth
                type="number"
                min={1}
                step={1}
                aria-label="Daily min. budget (USD)"
                placeholder="e.g. 50"
                value={value.dailyBudget}
                isError={showBudgetError && budgetInvalid}
                disabled={disabled}
                onChange={e =>
                  onChange({ ...value, dailyBudget: e.target.value })
                }
                onBlur={onBudgetBlur}
              />
              {showBudgetError && budgetInvalid && (
                <span className="text-xs text-states-error">
                  Enter a daily budget greater than 0.
                </span>
              )}
            </div>
          </div>
          <span className="text-[11px] text-gray-400">
            Attribution: incremental · 7-day click, 1-day view
          </span>
        </div>
      )}
    </div>
  )
}

interface CreativeTestSectionProps {
  value: CreativeTestState
  sourceCount: number
  metaSelectedCount: number
  /** Name of the single selected Meta ad set, when exactly one is selected. */
  adSetName?: string
  eligibility?: CreativeTestEligibility
  eligibilityLoading: boolean
  eligibilityError: boolean
  /** Validation errors to show — only after the user tried to launch. */
  errors: string[]
  showErrors: boolean
  disabled: boolean
  onChange: (next: CreativeTestState) => void
}

const BUDGET_MODE_OPTIONS: TSelectOption[] = [
  { value: "daily", label: "Daily amount (daily-budget ad set)" },
  { value: "lifetime", label: "Share of lifetime budget" },
]

function dollars(cents: number | null | undefined): string {
  return cents == null ? "—" : formatNumber(cents / 100, { currency: true })
}

/**
 * "Run as a Meta creative test" (MD-5569). The selected sources become
 * 2–7 variant ads inside ONE existing Meta ad set, then the job publishes
 * an ad study that holds a share of that ad set's budget for them (split
 * evenly, no audience overlap) and names the high performers. The original
 * ads keep running outside the test; the variants stay in the ad set
 * afterwards. Meta requires Highest volume bidding — the pre-flight below
 * reads the ad set live and blocks the launch on hard reasons.
 */
function CreativeTestSection({
  value,
  sourceCount,
  metaSelectedCount,
  adSetName,
  eligibility,
  eligibilityLoading,
  eligibilityError,
  errors,
  showErrors,
  disabled,
  onChange,
}: CreativeTestSectionProps) {
  const suggested = suggestedDailyBudgetDollars(eligibility)
  const adSet = eligibility?.adSet
  const variantNoun = `variant${sourceCount === 1 ? "" : "s"}`
  return (
    <div className="flex flex-col gap-system-2xs">
      <CheckboxWithLabel
        className="checkbox checkbox-sm"
        checked={value.enabled}
        disabled={disabled}
        onChange={e => onChange({ ...value, enabled: e.target.checked })}
      >
        <span className="text-sm font-medium text-white">
          Run as a Meta creative test
        </span>
      </CheckboxWithLabel>
      <span className="text-xs text-gray-300">
        The selected sources become {CREATIVE_TEST_MIN_VARIANTS}–
        {CREATIVE_TEST_MAX_VARIANTS} variants inside one existing ad set. Meta
        holds a share of that ad set&apos;s budget for them, splits it evenly
        with no audience overlap, and names the high performers. The original
        ads keep running; the variants stay in the ad set afterwards. Needs
        Highest volume bidding.
      </span>
      {value.enabled && (
        <div className="flex flex-col gap-system-2xs pt-system-2xs">
          <div className="text-xs text-gray-300">
            {sourceCount} {variantNoun} →{" "}
            {metaSelectedCount === 1 && adSetName
              ? adSetName
              : `${metaSelectedCount} ad sets selected (pick exactly one)`}
          </div>

          {metaSelectedCount === 1 && (
            <div
              className="rounded-md border border-basic-blue px-system-sm py-system-2xs text-xs"
              data-testid="creative-test-preflight"
            >
              {eligibilityLoading && (
                <span className="text-gray-300">Checking the ad set…</span>
              )}
              {!eligibilityLoading && eligibilityError && (
                <span className="text-states-error">
                  Couldn&apos;t read the ad set from Meta. Try again, or check
                  the Meta connection.
                </span>
              )}
              {!eligibilityLoading && adSet && (
                <div className="flex flex-col gap-[2px]">
                  <span
                    className={cn(
                      "font-medium",
                      eligibility?.eligible
                        ? "text-system-green-500"
                        : "text-states-error",
                    )}
                  >
                    {eligibility?.eligible
                      ? "Ad set is eligible"
                      : "Ad set is not eligible"}
                  </span>
                  <span className="text-gray-300">
                    Delivery {adSet.effectiveStatus ?? adSet.status} · bid
                    strategy {adSet.bidStrategy ?? "unknown"} (
                    {adSet.budgetLevel.toLowerCase()} budget) ·{" "}
                    {adSet.dailyBudget != null
                      ? `${dollars(adSet.dailyBudget)}/day`
                      : `${dollars(adSet.lifetimeBudget)} lifetime`}
                    {eligibility?.spendLast7d != null &&
                      ` · ${formatNumber(eligibility.spendLast7d, { currency: true, decimals: 0 })} spent in 7d`}
                    {eligibility &&
                      eligibility.activeStudies.length > 0 &&
                      ` · ${eligibility.activeStudies.length} active study`}
                  </span>
                  {eligibility?.reasons.map(reason => (
                    <span key={reason} className="text-states-error">
                      {reason}
                    </span>
                  ))}
                  {eligibility?.warnings.map(warning => (
                    <span key={warning} className="text-amber-300">
                      {warning}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-system-sm sm:grid-cols-2">
            <label className="flex flex-col gap-[2px] text-xs text-gray-300">
              Test name
              <Input
                fullWidth
                aria-label="Creative test name"
                placeholder="e.g. Hooks round 1"
                value={value.name}
                isError={showErrors && !value.name.trim()}
                disabled={disabled}
                onChange={e => onChange({ ...value, name: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-[2px] text-xs text-gray-300">
              Test budget
              <Select
                aria-label="Test budget mode"
                options={BUDGET_MODE_OPTIONS}
                value={value.budgetMode}
                disabled={disabled}
                onChange={e =>
                  onChange({
                    ...value,
                    budgetMode: e.target
                      .value as CreativeTestState["budgetMode"],
                  })
                }
              />
            </label>
            {value.budgetMode === "daily" ? (
              <div className="flex flex-col gap-[2px] text-xs text-gray-300">
                Daily test budget (USD), split across the variants
                <Input
                  fullWidth
                  type="number"
                  min={1}
                  step={1}
                  aria-label="Daily test budget (USD)"
                  placeholder={
                    suggested != null ? String(suggested) : "e.g. 200"
                  }
                  value={value.dailyBudget}
                  disabled={disabled}
                  onChange={e =>
                    onChange({ ...value, dailyBudget: e.target.value })
                  }
                />
                {suggested != null && (
                  <button
                    type="button"
                    className="self-start text-[11px] text-brand hover:text-basic-pink-hover"
                    disabled={disabled}
                    onClick={() =>
                      onChange({ ...value, dailyBudget: String(suggested) })
                    }
                  >
                    Use Meta&apos;s suggested {CREATIVE_TEST_SUGGESTED_SHARE}%:{" "}
                    {formatNumber(suggested, { currency: true })}/day
                  </button>
                )}
              </div>
            ) : (
              <label className="flex flex-col gap-[2px] text-xs text-gray-300">
                Share of the lifetime budget (%)
                <Input
                  fullWidth
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  aria-label="Lifetime budget share (%)"
                  value={value.lifetimePercentage}
                  disabled={disabled}
                  onChange={e =>
                    onChange({ ...value, lifetimePercentage: e.target.value })
                  }
                />
              </label>
            )}
            <div className="grid grid-cols-2 gap-system-sm">
              <label className="flex flex-col gap-[2px] text-xs text-gray-300">
                Start date
                <Input
                  fullWidth
                  type="date"
                  aria-label="Creative test start date"
                  value={value.startDate}
                  disabled={disabled}
                  onChange={e =>
                    onChange({ ...value, startDate: e.target.value })
                  }
                />
              </label>
              <label className="flex flex-col gap-[2px] text-xs text-gray-300">
                Length (days)
                <Input
                  fullWidth
                  type="number"
                  min={1}
                  step={1}
                  aria-label="Creative test length (days)"
                  value={value.days}
                  disabled={disabled}
                  onChange={e => onChange({ ...value, days: e.target.value })}
                />
              </label>
            </div>
          </div>
          {showErrors && errors.length > 0 && (
            <ul className="list-disc pl-4 text-xs text-states-error">
              {errors.map(error => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default function BlastTargetPickerModal({
  open,
  onClose,
  sources,
}: BlastTargetPickerModalProps) {
  const [search, setSearch] = useState("")
  // Debounced mirror of `search` — RTK Query keys its cache (and dedupes
  // in-flight requests) by the serialized args, so feeding it `search`
  // directly fires one Meta `searchAdSets` call per keystroke. The
  // debounced value coalesces a burst of keystrokes into one query. RTK
  // Query also drops the result of any superseded subscription, so the
  // race ("older response wins because it lands last") can't happen even
  // without an explicit AbortController — by the time a stale response
  // arrives, the cache slot it would write to has no active subscriber
  // and the UI is already mounted against the new args' cache slot.
  const [debouncedSearch, setDebouncedSearch] = useState("")
  // Per platform, id → what launch needs to know about the ad set (see
  // SelectedAdSet). Keyed by platform because the tree shows one platform at
  // a time (tab) while a draft blast can fan out to Meta AND TikTok ad sets
  // in the same job. A Map (not a Set of ids) because the launch-time guards
  // need the name and PV flag of everything selected, and `adSets` only holds
  // the current search's results — a selected row can drop out of it when
  // the user keeps typing, so those must be captured at toggle time.
  const [selectedByPlatform, setSelectedByPlatform] = useState<
    Record<BlastPlatform, Map<string, SelectedAdSet>>
  >({ meta: new Map(), tiktok: new Map() })
  // Destination URL for social-post sources blasting into non-PV ad sets.
  // `destinationUrlTouched` gates the error styling so the field doesn't
  // open in red; `destinationUrlEditedRef` stops the company-default prefill
  // from overwriting something the user already typed.
  const [destinationUrl, setDestinationUrl] = useState("")
  const [destinationUrlTouched, setDestinationUrlTouched] = useState(false)
  const destinationUrlEditedRef = useRef(false)
  // Scheduling: "now" fires immediately; "later" sends an `at` instant so the
  // backend lands the job in SCHEDULED status until due. Defaults to blast-now.
  const [schedule, setSchedule] = useState<BlastScheduleValue>({
    mode: "now",
    iso: null,
    isValid: true,
  })
  const handleScheduleChange = useCallback(
    (value: BlastScheduleValue) => setSchedule(value),
    [],
  )
  // Names of selected ad sets awaiting the "non-testing ad set" confirmation.
  // Non-null renders the type-CONFIRM overlay; null means no pending gate.
  const [nonTestAdSetNames, setNonTestAdSetNames] = useState<string[] | null>(
    null,
  )
  // "Launch each ad into its own 1:1 ad set" (MD-5426). While enabled the
  // tree picks CAMPAIGNS (id → name, captured at toggle time for the same
  // reason as `selectedAdSets`) and the job creates the ad sets itself.
  // `budgetTouched` gates the budget field's error styling.
  const [oneToOne, setOneToOne] =
    useState<OneToOneAdSetState>(DEFAULT_ONE_TO_ONE)
  const [selectedCampaigns, setSelectedCampaigns] = useState<
    Map<string, { name: string }>
  >(new Map())
  const [budgetTouched, setBudgetTouched] = useState(false)
  // "Run as a Meta creative test" (MD-5569): the sources become variants in
  // ONE selected Meta ad set and the job publishes the ad study afterwards.
  // `creativeTestTouched` gates the error list so it only shows after a
  // launch attempt.
  const [creativeTest, setCreativeTest] = useState<CreativeTestState>(
    DEFAULT_CREATIVE_TEST,
  )
  const [creativeTestTouched, setCreativeTestTouched] = useState(false)

  // 300ms is the same cadence the CreativesTab search box uses — long
  // enough to catch a full burst of typing, short enough that users don't
  // wonder why the list stalled. Clears on unmount via the cleanup return.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, 300)
    return () => window.clearTimeout(id)
  }, [search])

  // Meta is always available. The TikTok tab appears when the company has the
  // TikTok Ads Management connector connected AND the selection contains at
  // least one source that can target TikTok — an AdForge draft, or a launched
  // TikTok ad. Meta-library entries and social posts stay Meta-only, and so
  // does 1:1 mode (the job creates Meta ad sets), which retracts the tab.
  const isTikTokConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.TIKTOK_ADS_MANAGEMENT,
  )
  const tiktokCapableSources = useMemo(
    () =>
      sources.filter(
        s => s.kind === "draft" || (s.kind === "ad" && s.platform === "tiktok"),
      ),
    [sources],
  )
  const showTikTokTab =
    isTikTokConnected &&
    tiktokCapableSources.length > 0 &&
    !oneToOne.enabled &&
    !creativeTest.enabled
  const [activePlatform, setActivePlatform] = useState<BlastPlatform>("meta")
  // A sources change (or enabling 1:1) can retract the TikTok tab while it's
  // active.
  useEffect(() => {
    if (!showTikTokTab && activePlatform !== "meta") {
      setActivePlatform("meta")
    }
  }, [showTikTokTab, activePlatform])

  // The tree renders the active platform's selection; launch reads both.
  const selectedAdSets = selectedByPlatform[activePlatform]
  const metaSelectedCount = selectedByPlatform.meta.size
  const tiktokSelectedCount = selectedByPlatform.tiktok.size
  const totalSelectedCount = metaSelectedCount + tiktokSelectedCount

  const { data: adSets, isFetching: adSetsLoading } = useSearchAdSetsQuery(
    { platform: activePlatform, search: debouncedSearch || undefined },
    { skip: !open },
  )

  const [resolveAssets] = useResolveAssetsMutation()
  const [analyzeMedia] = useAnalyzeMediaMutation()
  const waitForJob = useWaitForJob()
  const { categories: tagCategories } = useTagCategories()
  // True from the moment Launch is clicked until the job is enqueued (or
  // fails). The mutation's own isLoading only covers the final request; the
  // asset resolution that precedes it would otherwise leave the button live
  // and a second click could enqueue a duplicate.
  const [isLaunching, setIsLaunching] = useState(false)
  const [triggerAdBlast, { isLoading: isTriggering }] =
    useTriggerAdBlastMutation()
  const isBlasting = isLaunching || isTriggering

  // Company's required-tags rule (categories the ad-name template references).
  // Only queried while the modal is open — most page loads never open it.
  const { labelForSlug, missingFor, validate } = useRequiredAdTags({
    skip: !open,
  })

  // Social-post sources have no link of their own, so every non-PV target
  // needs a destination URL. The company's AdForge default is the same
  // fallback the server would apply, so seed the field with it — the user
  // still sees and confirms the URL instead of the job silently using it.
  const hasPostSources = useMemo(
    () => sources.some(source => source.kind === "post"),
    [sources],
  )
  const postCount = useMemo(
    () => sources.filter(source => source.kind === "post").length,
    [sources],
  )
  const { data: adForgeDefaults } = useAdForgeGetDefaultsQuery(undefined, {
    skip: !open || !hasPostSources,
  })
  useEffect(() => {
    if (!open || destinationUrlEditedRef.current) return
    const fallback = adForgeDefaults?.destinationUrl?.trim()
    if (fallback) setDestinationUrl(fallback)
  }, [open, adForgeDefaults])

  // Posts are Meta-only, so only the Meta selection can demand the URL.
  const nonPvSelectedCount = useMemo(() => {
    let count = 0
    for (const entry of selectedByPlatform.meta.values()) {
      if (!entry.profileVisit) count += 1
    }
    return count
  }, [selectedByPlatform])
  // In 1:1 mode every new ad set shares the chosen goal, so either all of
  // them need the URL (non-PV goal) or none do (Profile Visit goal).
  const oneToOneNonPvCount =
    oneToOne.enabled && !isProfileVisitGoal(oneToOne.optimizationGoal)
      ? selectedCampaigns.size
      : 0
  const nonPvTargetCount = oneToOne.enabled
    ? oneToOneNonPvCount
    : nonPvSelectedCount
  const needsDestinationUrl = hasPostSources && nonPvTargetCount > 0
  const destinationUrlMissing =
    needsDestinationUrl && !isValidHttpUrl(destinationUrl)
  const targetCount = oneToOne.enabled
    ? selectedCampaigns.size
    : totalSelectedCount
  const targetNoun = oneToOne.enabled ? "campaign" : "ad set"
  const budgetInvalid =
    oneToOne.enabled && parseDailyBudget(oneToOne.dailyBudget) === null

  // Creative test: the single selected Meta ad set is read live from Meta
  // (bid strategy, delivery, budget, active studies) while the mode is on.
  const creativeTestAdSetId =
    creativeTest.enabled && metaSelectedCount === 1
      ? (Array.from(selectedByPlatform.meta.keys())[0] ?? null)
      : null
  const creativeTestAdSetName = creativeTestAdSetId
    ? selectedByPlatform.meta.get(creativeTestAdSetId)?.name
    : undefined
  const {
    data: creativeTestEligibility,
    isFetching: creativeTestEligibilityLoading,
    isError: creativeTestEligibilityError,
  } = useGetCreativeTestEligibilityQuery(creativeTestAdSetId ?? "", {
    skip: !open || !creativeTestAdSetId,
  })
  const creativeTestValidationErrors = useMemo(
    () =>
      creativeTestErrors(creativeTest, {
        sourceCount: sources.length,
        metaSelectedCount,
        tiktokSelectedCount,
        eligibility: creativeTestEligibility,
        scheduledAt: schedule.mode === "later" ? schedule.iso : null,
      }),
    [
      creativeTest,
      sources.length,
      metaSelectedCount,
      tiktokSelectedCount,
      creativeTestEligibility,
      schedule,
    ],
  )
  const creativeTestInvalid =
    creativeTest.enabled &&
    (creativeTestValidationErrors.length > 0 ||
      creativeTestEligibilityLoading ||
      (creativeTestAdSetId !== null && !creativeTestEligibility))

  // Flipping the 1:1 mode swaps what the tree selects (ad sets ↔ campaigns),
  // so both selections — and any pending CONFIRM gate built from them — are
  // cleared to keep the launch payload consistent with what's on screen.
  // 1:1 and creative test are mutually exclusive (a test needs an existing,
  // delivering ad set), so turning 1:1 on switches the test off.
  const handleOneToOneChange = useCallback(
    (next: OneToOneAdSetState) => {
      if (next.enabled !== oneToOne.enabled) {
        setSelectedByPlatform({ meta: new Map(), tiktok: new Map() })
        setSelectedCampaigns(new Map())
        setNonTestAdSetNames(null)
      }
      if (next.enabled) setCreativeTest(prev => ({ ...prev, enabled: false }))
      setOneToOne(next)
    },
    [oneToOne.enabled],
  )
  // Enabling the test drops any TikTok selection (Meta only) and seeds the
  // start date to tomorrow the first time, so the form opens launchable.
  const handleCreativeTestChange = useCallback(
    (next: CreativeTestState) => {
      const enabling = next.enabled && !creativeTest.enabled
      if (enabling) {
        setSelectedByPlatform(prev => ({ meta: prev.meta, tiktok: new Map() }))
        setNonTestAdSetNames(null)
      }
      setCreativeTest(
        enabling && !next.startDate
          ? { ...next, startDate: defaultStartDate() }
          : next,
      )
    },
    [creativeTest.enabled],
  )
  const handleBudgetBlur = useCallback(() => setBudgetTouched(true), [])

  const handleDestinationUrlChange = useCallback((next: string) => {
    destinationUrlEditedRef.current = true
    setDestinationUrl(next)
  }, [])
  const handleDestinationUrlBlur = useCallback(() => {
    setDestinationUrlTouched(true)
    // Repair paste mistakes (bare domain, doubled scheme) the way AdForge's
    // URL field does, so what the user sees is what the job sends.
    setDestinationUrl(prev => {
      const normalized = normalizeUrl(prev)
      return normalized === prev ? prev : normalized
    })
  }, [])

  // Inline tag fixing: a draft missing required tags can be tagged right here
  // via the same "Build ad name from tags" composer AdForge uses, instead of
  // forcing a round-trip to the AdForge editor. Saving PATCHes the draft
  // (tags + the composed convention name); the mutation invalidates the
  // drafts list, so the calling tab's refreshed rows flow back in through
  // `sources` and the missing-tags warning clears on its own.
  const [tagTarget, setTagTarget] = useState<Extract<
    BlastSource,
    { kind: "draft" | "post" }
  > | null>(null)
  const [updateCreative] = useAdForgeUpdateCreativeMutation()

  // Social posts have no draft to persist tags on: the selection (and the
  // composed ad name) is held here per post until launch, then rides on the
  // blast payload so the API tags every ad it creates — the same "every ad
  // carries its tags" rule AdForge drafts already follow.
  const [postTags, setPostTags] = useState<
    Map<string, { name: string; tags: Record<string, string[]> }>
  >(new Map())

  // Drafts whose tag save is in flight, keyed to the `tags` reference they had
  // when the save started. The warning row only clears once the PATCH lands
  // AND the invalidated drafts list refetches back through `sources` — without
  // an indicator that window reads as "the save did nothing". An entry is
  // dropped when its source's tags identity changes (the refetch arrived —
  // even a partial re-pick counts) or the save fails.
  const [savingTags, setSavingTags] = useState<
    Map<string, Record<string, string[]> | null | undefined>
  >(new Map())

  useEffect(() => {
    setSavingTags(prev => {
      if (prev.size === 0) return prev
      let changed = false
      const next = new Map(prev)
      for (const [id, snapshot] of prev) {
        const src = sources.find(s => s.kind === "draft" && s.id === id)
        if (!src || src.kind !== "draft" || src.tags !== snapshot) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [sources])

  // Group by campaign so the tree picker matches the AdForge launcher's
  // layout — every ad set under its parent campaign with a campaign-level
  // toggle that flips all children.
  const adSetsByCampaign = useMemo(() => {
    if (!adSets) return []
    const map = new Map<string, { id: string; name: string; adSets: AdSet[] }>()
    for (const adSet of adSets) {
      const cId = adSet.campaign?.id || "unknown"
      const cName = adSet.campaign?.name || "Unknown campaign"
      const entry = map.get(cId) ?? { id: cId, name: cName, adSets: [] }
      entry.adSets.push(adSet)
      map.set(cId, entry)
    }
    return Array.from(map.values())
  }, [adSets])

  // Both toggles write into the ACTIVE platform's map: the rows on screen
  // always belong to `activePlatform` (the search query is keyed by it).
  const toggleAdSet = useCallback(
    (adSet: AdSet) => {
      const id = String(adSet.id)
      setSelectedByPlatform(prev => {
        const next = new Map(prev[activePlatform])
        if (next.has(id)) next.delete(id)
        else {
          next.set(id, {
            name: adSet.name,
            profileVisit: isProfileVisitAdSet(adSet),
          })
        }
        return { ...prev, [activePlatform]: next }
      })
    },
    [activePlatform],
  )

  const toggleCampaign = useCallback(
    (campaignAdSets: AdSet[]) => {
      setSelectedByPlatform(prev => {
        const next = new Map(prev[activePlatform])
        const allOn = campaignAdSets.every(a => next.has(String(a.id)))
        if (allOn) {
          for (const adSet of campaignAdSets) next.delete(String(adSet.id))
        } else {
          for (const adSet of campaignAdSets) {
            next.set(String(adSet.id), {
              name: adSet.name,
              profileVisit: isProfileVisitAdSet(adSet),
            })
          }
        }
        return { ...prev, [activePlatform]: next }
      })
    },
    [activePlatform],
  )

  // 1:1 mode: the campaign row IS the target. Ad sets without a campaign are
  // grouped under "unknown", which can't host a new ad set — the row renders
  // disabled and this is a no-op for it.
  const toggleCampaignOneToOne = useCallback(
    (campaign: { id: string; name: string }) => {
      if (campaign.id === "unknown") return
      setSelectedCampaigns(prev => {
        const next = new Map(prev)
        if (next.has(campaign.id)) next.delete(campaign.id)
        else next.set(campaign.id, { name: campaign.name })
        return next
      })
    },
    [],
  )

  // Sources that don't satisfy the company's required-tags rule. Blasting them
  // would create ads whose names claim a dimension they carry no tag for, so
  // the launch is blocked here (and rejected by the API) until the gap is
  // filled. Drafts carry their tags from AdForge; social posts are tagged
  // right here (see `postTags`). Ads and Meta-library creatives clone an
  // already-tagged ad, so they are not covered.
  const untaggedSources = useMemo(() => {
    const entries: Array<{
      source: Extract<BlastSource, { kind: "draft" | "post" }>
      missing: string[]
      needsAnyTag: boolean
    }> = []
    for (const source of sources) {
      if (source.kind === "draft") {
        const missing = missingFor(source.tags)
        if (missing.length > 0) {
          entries.push({ source, missing, needsAnyTag: false })
        }
      } else if (source.kind === "post") {
        const verdict = validate(postTags.get(source.postId)?.tags)
        if (verdict) entries.push({ source, ...verdict })
      }
    }
    return entries
  }, [sources, missingFor, validate, postTags])
  const hasUntaggedSources = untaggedSources.length > 0

  // TikTok is video-only: block launch while TikTok ad sets are selected and
  // a draft in the selection is known to carry no video. `undefined` means the
  // calling tab didn't check — the API still rejects at prepare time.
  const videolessDraftsForTikTok = useMemo(
    () =>
      tiktokSelectedCount === 0
        ? []
        : sources.filter(
            (source): source is Extract<BlastSource, { kind: "draft" }> =>
              source.kind === "draft" && source.hasVideoAsset === false,
          ),
    [sources, tiktokSelectedCount],
  )
  const hasVideolessDraftsForTikTok = videolessDraftsForTikTok.length > 0

  // Every source must resolve to at least one target: a Meta-only source
  // (library entry, post, launched Meta ad) with only TikTok ad sets selected
  // would enqueue a unit with an empty target list. Moot in 1:1 mode, where
  // every source targets the selected Meta campaigns.
  const sourcesWithoutTargets = useMemo(
    () =>
      totalSelectedCount === 0
        ? []
        : sources.filter(source => {
            const canMeta = source.kind !== "ad" || source.platform === "meta"
            const canTikTok =
              source.kind === "draft" ||
              (source.kind === "ad" && source.platform === "tiktok")
            return (
              !(canMeta && metaSelectedCount > 0) &&
              !(canTikTok && tiktokSelectedCount > 0)
            )
          }),
    [sources, totalSelectedCount, metaSelectedCount, tiktokSelectedCount],
  )
  const hasSourcesWithoutTargets = sourcesWithoutTargets.length > 0

  const handleClose = useCallback(() => {
    setSelectedByPlatform({ meta: new Map(), tiktok: new Map() })
    setActivePlatform("meta")
    setSelectedCampaigns(new Map())
    setOneToOne(DEFAULT_ONE_TO_ONE)
    setBudgetTouched(false)
    setPostTags(new Map())
    setSearch("")
    setDebouncedSearch("")
    setSchedule({ mode: "now", iso: null, isValid: true })
    setNonTestAdSetNames(null)
    setDestinationUrl("")
    setDestinationUrlTouched(false)
    destinationUrlEditedRef.current = false
    onClose()
  }, [onClose])

  type PostSource = Extract<BlastSource, { kind: "post" }>
  type PostMedia = {
    imageUrl?: string
    videoUrl?: string
    thumbnailUrl?: string
  }

  // Post sources carry their media as the cascade's rebuild fallback (used
  // when Meta refuses to boost the post as-is — 1487472 "Page Post Can't Be
  // Used" — and the token can't read the post either) and as the input to
  // the AI tag suggester. Meta must be able to fetch those URLs, so resolve
  // them to our own S3 copies through the asset manager (same path the
  // previews render through) rather than sending the raw platform CDN links,
  // which expire. Resolution is best-effort: a miss falls back to the raw
  // URLs so the blast is never blocked here.
  const resolvePostMedia = useCallback(
    async (postSources: PostSource[]): Promise<Map<string, PostMedia>> => {
      const postMedia = new Map<string, PostMedia>()
      if (postSources.length === 0) return postMedia
      // One entry per post: the resolve request plus the key the API echoes
      // it back under, so the lookup never depends on array positions.
      const entries = postSources.map(source => {
        const sourceType = mapMediaChannelToSourceType(
          source.channel ?? "facebook",
          true,
        )
        return {
          source,
          key: `${sourceType}:${source.postId}`,
          request: {
            sourceType,
            externalRefId: source.postId,
            ...(source.previewUrl ? { url: source.previewUrl } : {}),
          },
        }
      })
      type ResolvedAsset = {
        url?: string
        thumbnailUrl?: string
        mediaType?: string
        syncStatus?: string
      }
      let resolved: Record<string, ResolvedAsset | null> = {}
      try {
        resolved = (
          await resolveAssets({ assets: entries.map(e => e.request) }).unwrap()
        ).results
      } catch {
        resolved = {}
      }
      for (const { source: s, key } of entries) {
        const asset = resolved[key]
        const synced = asset?.syncStatus === "SYNCED" ? asset : undefined
        const isVideo = synced?.mediaType === "VIDEO"
        const syncedImage = isVideo ? synced?.thumbnailUrl : synced?.url
        const syncedVideo = isVideo ? synced?.url : undefined
        // A cached preview that is itself a video file must never be sent
        // as the image (Meta 2446603 "Invalid Image In Ad").
        const previewIsVideo = !!s.previewUrl && isVideoUrl(s.previewUrl)
        const rawImage = previewIsVideo
          ? undefined
          : (s.previewUrl ?? undefined)
        postMedia.set(s.postId, {
          imageUrl: syncedImage ?? rawImage,
          videoUrl: syncedVideo ?? s.videoUrl,
          thumbnailUrl: synced?.thumbnailUrl ?? rawImage,
        })
      }
      return postMedia
    },
    [resolveAssets],
  )

  // "Suggest with AI" for a social post: analyze its media (video preferred,
  // else image) with the company taxonomy and hand the composer a selection.
  // The analysis is an `analyze-media` job (videos take ~30-90s, past the
  // 29s gateway limit), so enqueue and poll for the result.
  const suggestPostTags = useCallback(
    async (source: PostSource): Promise<Record<string, string[]>> => {
      const media = (await resolvePostMedia([source])).get(source.postId)
      const url = media?.videoUrl ?? media?.imageUrl
      if (!url) {
        throw new Error("This post has no media we can analyze.")
      }
      const { jobRunId } = await analyzeMedia({
        url,
        mediaType: media?.videoUrl ? "video" : "image",
      }).unwrap()
      const job = await waitForJob(jobRunId)
      if (job.status !== "COMPLETED") {
        throw new Error(
          job.error ?? `Media analysis ${job.status.toLowerCase()}.`,
        )
      }
      const result = job.result as AnalyzeMediaJobResult | null
      if (!result?.suggestedTags) {
        throw new Error("Media analysis returned no tag suggestions.")
      }
      return suggestionsToSelection(result.suggestedTags, tagCategories)
    },
    [resolvePostMedia, analyzeMedia, waitForJob, tagCategories],
  )

  const launchBlast = useCallback(async () => {
    // Scheduling guard, re-checked at fire time: handleLaunch already ran it,
    // but the non-testing CONFIRM dialog can sit open long enough for a
    // "later" instant to lapse before the user clicks Launch anyway.
    // `schedule.isValid` is a snapshot from the control's last onChange, so
    // the lapse is caught by comparing the instant against the clock.
    const scheduledAt = schedule.mode === "later" ? schedule.iso : undefined
    if (
      schedule.mode === "later" &&
      (!scheduledAt ||
        !schedule.isValid ||
        !dayjs(scheduledAt).isAfter(dayjs()))
    ) {
      toast.error("Pick a date and time in the future to schedule the blast.")
      return
    }

    const metaIds = Array.from(selectedByPlatform.meta.keys())
    const tiktokIds = Array.from(selectedByPlatform.tiktok.keys())
    // Where the ads go: existing ad sets (per-platform key) or — in 1:1 mode
    // — new ad sets the job creates under each selected campaign. The two
    // are mutually exclusive on the wire, so exactly one key is ever set.
    // 1:1 is Meta-only; the TikTok tab is hidden (and its selection cleared)
    // while it's on, so `ids` is always the Meta list then.
    const oneToOneDto = () => ({
      one_to_one_adsets: {
        campaignIds: Array.from(selectedCampaigns.keys()),
        adSetType: oneToOne.adSetType,
        optimizationGoal: oneToOne.optimizationGoal,
        dailyBudget: parseDailyBudget(oneToOne.dailyBudget) ?? 0,
      },
    })
    const targetDtoFor = (unitPlatform: string, ids: string[]) =>
      oneToOne.enabled
        ? oneToOneDto()
        : { target_adsets: { [unitPlatform]: ids } }
    // AdForge drafts are the one source kind that can fan out to Meta AND
    // TikTok ad sets in the same unit.
    const draftTargetDto = () =>
      oneToOne.enabled
        ? oneToOneDto()
        : {
            target_adsets: {
              ...(metaIds.length ? { meta: metaIds } : {}),
              ...(tiktokIds.length ? { tiktok: tiktokIds } : {}),
            },
          }
    const postDestinationUrl = needsDestinationUrl
      ? normalizeUrl(destinationUrl)
      : undefined

    const postMedia = await resolvePostMedia(
      sources.filter(
        (src): src is Extract<BlastSource, { kind: "post" }> =>
          src.kind === "post",
      ),
    )

    // Selecting multiple sources fans out to one BULK ad-blast job (one job
    // covering every source × ad set), not one job per source. AdForge "draft"
    // creatives join the same job as `adCreative` units, so a mixed selection
    // is launched as a single durable, resumable background job.
    const units: AdBlastUnitInput[] = []

    for (const source of sources) {
      if (source.kind === "meta") {
        // Meta-library reblast reuses the existing creative_id verbatim.
        units.push({
          kind: "creative",
          creativeId: source.id,
          dto: {
            ...targetDtoFor("meta", metaIds),
            ...(source.name ? { adName: source.name } : {}),
          },
        })
      } else if (source.kind === "ad") {
        units.push({
          kind: "ad",
          platform: source.platform,
          adId: source.externalId,
          dto: {
            // A launched ad clones within its own platform only (a Meta
            // creative can't be rebuilt on TikTok and vice versa).
            ...targetDtoFor(
              source.platform,
              source.platform === "tiktok" ? tiktokIds : metaIds,
            ),
            ...(source.name ? { overrides: { adName: source.name } } : {}),
          },
        })
      } else if (source.kind === "post") {
        // The destination URL only matters for non-PV targets; PV ad sets
        // strip it server-side (strategy `removeLink`) and use the IG
        // profile URL. Sending it whenever any target needs it keeps the
        // payload simple — mixed PV + non-PV selections get the right
        // behaviour per ad set from the cascade.
        const media = postMedia.get(source.postId)
        const tagged = postTags.get(source.postId)
        const postOverrides = {
          ...(postDestinationUrl ? { destinationUrl: postDestinationUrl } : {}),
          ...(tagged?.name ? { adName: tagged.name } : {}),
        }
        units.push({
          kind: "post",
          platform: source.platform,
          postId: source.postId,
          dto: {
            // Organic posts promote via object_story_id — Meta-only.
            ...targetDtoFor(source.platform, metaIds),
            ...(source.channel ? { channel: source.channel } : {}),
            ...(tagged ? { tags: tagged.tags } : {}),
            ...(media?.imageUrl ? { imageUrl: media.imageUrl } : {}),
            ...(source.primaryText ? { caption: source.primaryText } : {}),
            ...(media?.videoUrl
              ? {
                  videoUrl: media.videoUrl,
                  mediaType: "video" as const,
                  ...(media.thumbnailUrl
                    ? { thumbnailUrl: media.thumbnailUrl }
                    : {}),
                }
              : {}),
            ...(Object.keys(postOverrides).length > 0
              ? { overrides: postOverrides }
              : {}),
          },
        })
      } else {
        // AdForge draft creative — fanned out in the background just like the
        // others (drafts are Meta-only today, so target the meta ad sets).
        units.push({
          kind: "adCreative",
          adCreativeId: source.id,
          dto: {
            ...draftTargetDto(),
          },
        })
      }
    }

    // One job for every selected source (ads, posts, Meta creatives, drafts).
    // Awaited so the Blast button can show a spinner until the job is accepted
    // (202). On failure we keep the modal open so the user can retry without
    // rebuilding their ad-set selection.
    // Creative test (MD-5569): the batch carries the study block; the job
    // publishes the study once every variant is in the ad set.
    const creativeTestInput = creativeTest.enabled
      ? toCreativeTestInput(creativeTest)
      : null
    if (creativeTest.enabled && !creativeTestInput) {
      toast.error("Complete the creative test settings first.")
      return
    }
    try {
      await triggerAdBlast({
        input: {
          kind: "batch",
          units,
          ...(creativeTestInput ? { creativeTest: creativeTestInput } : {}),
        },
        at: scheduledAt ?? undefined,
      }).unwrap()
    } catch (err: unknown) {
      console.error("Failed to start blast job:", err)
      toast.error(
        scheduledAt
          ? "Couldn't schedule the blast job."
          : "Couldn't start the blast job.",
      )
      return
    }

    const sourceLabel = `${sources.length} source${sources.length === 1 ? "" : "s"}`
    const totalTargets = metaIds.length + tiktokIds.length
    const targetLabel = oneToOne.enabled
      ? `${selectedCampaigns.size} campaign${selectedCampaigns.size === 1 ? "" : "s"} (1:1 ad sets)`
      : `${totalTargets} ad set${totalTargets === 1 ? "" : "s"}`
    if (creativeTestInput) {
      toast.success(
        scheduledAt
          ? `Scheduled creative test "${creativeTestInput.name}" (${sourceLabel}) for ${formatScheduledFor(scheduledAt)} — we'll notify you when it runs`
          : `Started creative test "${creativeTestInput.name}": blasting ${sourceLabel} into the ad set, then publishing the study — we'll notify you when it's done`,
      )
    } else {
      toast.success(
        scheduledAt
          ? `Scheduled ${sourceLabel} to blast to ${targetLabel} on ${formatScheduledFor(scheduledAt)} — we'll notify you when it runs`
          : `Started blasting ${sourceLabel} to ${targetLabel} — we'll notify you when it's done`,
      )
    }
    handleClose()
  }, [
    triggerAdBlast,
    resolvePostMedia,
    selectedByPlatform,
    selectedCampaigns,
    oneToOne,
    creativeTest,
    postTags,
    sources,
    handleClose,
    schedule,
    needsDestinationUrl,
    destinationUrl,
  ])

  const runBlast = useCallback(async () => {
    if (isLaunching) return
    setIsLaunching(true)
    try {
      await launchBlast()
    } finally {
      setIsLaunching(false)
    }
  }, [isLaunching, launchBlast])

  const handleLaunch = useCallback(() => {
    if (targetCount === 0) return
    if (sources.length === 0) return
    if (isBlasting) return
    // 1:1 ad sets can't be created without a budget; the button is disabled
    // too, but surface the field's error state if we get here anyway.
    if (budgetInvalid) {
      setBudgetTouched(true)
      toast.error("Enter a daily min. budget for the 1:1 ad sets.")
      return
    }
    // Creative test: every blocker is listed under the section; the first
    // one goes in the toast so it's visible even if the section is scrolled
    // out of view.
    if (creativeTestInvalid) {
      setCreativeTestTouched(true)
      toast.error(
        creativeTestValidationErrors[0] ??
          "Waiting for the ad set's creative test pre-flight.",
      )
      return
    }
    // Belt-and-braces with the disabled button: a draft can lose its tags in
    // another tab between render and click.
    if (hasUntaggedSources) {
      toast.error(
        'Some sources are missing required tags. Use "Set tags" above to add them first.',
      )
      return
    }
    if (hasVideolessDraftsForTikTok) {
      toast.error(
        "TikTok ads require a video. Add a video to those drafts or deselect the TikTok ad groups.",
      )
      return
    }
    if (hasSourcesWithoutTargets) {
      toast.error(
        "Some selected creatives can't target any of the selected ad sets. Pick ad sets on their platform or deselect them.",
      )
      return
    }

    // Social posts into a non-PV ad set need somewhere to send the click.
    // Without this the job fails on Meta's "link field is required".
    if (destinationUrlMissing) {
      setDestinationUrlTouched(true)
      toast.error(
        "Enter a destination URL for the social posts before blasting to non-Profile-Visit ad sets.",
      )
      return
    }

    // Scheduling guard: block launch if "later" is picked but the instant is
    // missing or already in the past (the control also disables the button, but
    // this covers the moment between selecting a time and it lapsing).
    if (schedule.mode === "later" && (!schedule.iso || !schedule.isValid)) {
      toast.error("Pick a date and time in the future to schedule the blast.")
      return
    }

    // Safety gate (MD-5412): most blasts are meant for testing ad sets. Any
    // selected ad set — or, in 1:1 mode, campaign — whose name doesn't
    // contain "test" (which also matches "testing") diverts the launch to a
    // type-CONFIRM dialog instead of firing immediately.
    const targetNames = oneToOne.enabled
      ? Array.from(selectedCampaigns.values())
      : [
          ...selectedByPlatform.meta.values(),
          ...selectedByPlatform.tiktok.values(),
        ]
    const nonTest = targetNames
      .map(entry => entry.name)
      .filter(name => !/test/i.test(name))
    if (nonTest.length > 0) {
      setNonTestAdSetNames(nonTest)
      return
    }

    void runBlast()
  }, [
    targetCount,
    budgetInvalid,
    creativeTestInvalid,
    creativeTestValidationErrors,
    oneToOne.enabled,
    selectedCampaigns,
    selectedByPlatform,
    sources,
    isBlasting,
    hasUntaggedSources,
    hasVideolessDraftsForTikTok,
    hasSourcesWithoutTargets,
    destinationUrlMissing,
    schedule,
    runBlast,
  ])

  // Tooltip on the disabled Blast button explaining the first blocking reason.
  let launchBlockedReason: string | undefined
  if (hasUntaggedSources) {
    launchBlockedReason =
      "Some sources are missing the tags every ad must carry"
  } else if (hasVideolessDraftsForTikTok) {
    launchBlockedReason =
      "TikTok ads require a video — some selected drafts have none"
  } else if (hasSourcesWithoutTargets) {
    launchBlockedReason =
      "Some selected creatives have no ad sets on their platform selected"
  } else if (destinationUrlMissing) {
    launchBlockedReason = "Enter a destination URL for the social posts first"
  } else if (budgetInvalid) {
    launchBlockedReason = "Enter a daily min. budget for the 1:1 ad sets"
  } else if (creativeTestInvalid) {
    launchBlockedReason =
      creativeTestValidationErrors[0] ??
      "Checking the ad set for the creative test"
  }

  const sourceSummary = useMemo(() => {
    const draftCount = sources.filter(s => s.kind === "draft").length
    const metaCount = sources.filter(s => s.kind === "meta").length
    const adCount = sources.filter(s => s.kind === "ad").length
    const parts: string[] = []
    if (draftCount) {
      parts.push(`${draftCount} draft${draftCount === 1 ? "" : "s"}`)
    }
    if (adCount) parts.push(`${adCount} ad${adCount === 1 ? "" : "s"}`)
    if (metaCount) {
      parts.push(
        `${metaCount} Meta library entr${metaCount === 1 ? "y" : "ies"}`,
      )
    }
    return parts.join(" + ") || "nothing"
  }, [sources])

  return (
    <Dialog
      isOpen={open}
      handleClose={handleClose}
      // Width is bounded by `max-w-[95vw]` via classes — inline `width: 920`
      // becomes `min(920px, 95vw)` so the modal never overflows narrow viewports.
      // `max-h-[90vh] + flex flex-col` lets the inner panel cap itself to the
      // viewport with only the ad-set list scrolling.
      width={920}
      classes={{
        container:
          "flex max-h-[90vh] w-full max-w-[95vw] flex-col rounded-lg border border-basic-blue bg-basic-dark-blue text-left text-white shadow-xl",
      }}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-basic-blue px-system-md py-system-sm">
        <div>
          <h2 className="text-lg font-semibold">Blast to ad sets</h2>
          <p className="text-xs text-gray-300">
            Launching {sourceSummary} into each selected ad set.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          disabled={isBlasting}
          className="text-gray-300 hover:text-white disabled:opacity-50"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="shrink-0 px-system-md pt-system-sm">
        <SourcesPreview sources={sources} />
      </div>

      {hasUntaggedSources && (
        <div className="shrink-0 px-system-md pt-system-sm">
          <div className="flex gap-system-2xs rounded-md border border-system-yellow-600/50 bg-system-yellow-900/20 px-system-sm py-system-2xs">
            <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0 text-system-yellow-400" />
            <div className="flex flex-col gap-[2px] text-xs">
              <span className="font-medium text-system-yellow-300">
                Missing required tags — set them below (or deselect these) to
                blast.
              </span>
              {untaggedSources.map(({ source, missing, needsAnyTag }) => (
                <span
                  key={source.id}
                  className="flex flex-wrap items-center gap-x-2 text-gray-300"
                >
                  <span>
                    <span className="text-white">{source.name}</span> —{" "}
                    {needsAnyTag
                      ? "at least one tag"
                      : missing.map(labelForSlug).join(", ")}
                  </span>
                  {savingTags.has(source.id) ? (
                    <span className="flex items-center gap-1 font-medium text-system-yellow-300">
                      <span className="loading loading-spinner loading-xs" />
                      Saving…
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setTagTarget(source)}
                      disabled={isBlasting}
                      className="font-medium text-system-yellow-300 underline underline-offset-2 hover:text-system-yellow-200 disabled:opacity-50"
                    >
                      Set tags
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {hasVideolessDraftsForTikTok && (
        <div className="shrink-0 px-system-md pt-system-sm">
          <div className="flex gap-system-2xs rounded-md border border-system-yellow-600/50 bg-system-yellow-900/20 px-system-sm py-system-2xs">
            <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0 text-system-yellow-400" />
            <span className="text-xs font-medium text-system-yellow-300">
              TikTok ads require a video — these drafts have none:{" "}
              {videolessDraftsForTikTok.map(d => d.name).join(", ")}. Add a
              video in AdForge or deselect the TikTok ad groups.
            </span>
          </div>
        </div>
      )}

      {!isTikTokConnected && tiktokCapableSources.length > 0 && (
        <div className="shrink-0 px-system-md pt-system-sm">
          <p className="text-xs text-gray-400">
            Want to blast to TikTok too?{" "}
            <Link
              to={brandRoutes.SETUP.CONNECTORS}
              className="text-gray-200 underline underline-offset-2 hover:text-white"
            >
              Connect TikTok Ads Management
            </Link>{" "}
            to pick TikTok ad groups here.
          </p>
        </div>
      )}

      <div className="shrink-0 px-system-md pt-system-sm">
        {showTikTokTab && (
          <div className="mb-system-2xs flex gap-system-2xs">
            {(Object.keys(PLATFORM_TAB_LABEL) as BlastPlatform[]).map(p => {
              const count = selectedByPlatform[p].size
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setActivePlatform(p)}
                  className={cn(
                    "rounded-md border px-system-sm py-system-2xs text-xs font-medium",
                    activePlatform === p
                      ? "border-system-green-600 bg-basic-black text-white"
                      : "border-basic-blue text-gray-300 hover:text-white",
                  )}
                >
                  {PLATFORM_TAB_LABEL[p]}
                  {count > 0 ? ` (${count})` : ""}
                </button>
              )
            })}
          </div>
        )}
        <Input
          fullWidth
          type="search"
          aria-label="Search ad sets"
          placeholder={
            activePlatform === "tiktok"
              ? "Search TikTok campaigns and ad groups…"
              : "Search campaigns and ad sets…"
          }
          value={search}
          onChange={e => setSearch(e.target.value)}
          prependNode={
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-300" />
          }
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-system-md py-system-sm">
        <div className="rounded-md border border-basic-blue bg-basic-black">
          {adSetsLoading ? (
            <div className="py-system-md text-center text-sm text-gray-300">
              Loading ad sets…
            </div>
          ) : adSetsByCampaign.length === 0 ? (
            <div className="py-system-md text-center text-sm text-gray-300">
              {search
                ? `No ad sets match "${search}".`
                : "No ad sets available."}
            </div>
          ) : (
            <ul className="flex flex-col">
              {adSetsByCampaign.map((campaign, campaignIdx) => {
                const ids = campaign.adSets.map(a => String(a.id))
                // 1:1 mode: the campaign row is the target and its ad sets
                // are hidden — a new one is created per ad. Otherwise the
                // row is a select-all for its ad sets.
                const campaignOn = oneToOne.enabled
                  ? selectedCampaigns.has(campaign.id)
                  : ids.every(id => selectedAdSets.has(id))
                const someOn =
                  !oneToOne.enabled &&
                  !campaignOn &&
                  ids.some(id => selectedAdSets.has(id))
                const campaignDisabled =
                  oneToOne.enabled && campaign.id === "unknown"
                const onCampaignToggle = () => {
                  if (oneToOne.enabled) toggleCampaignOneToOne(campaign)
                  else toggleCampaign(campaign.adSets)
                }
                return (
                  <li
                    key={campaign.id}
                    className={cn(
                      campaignIdx !== 0 && "border-t border-basic-blue",
                    )}
                  >
                    <button
                      type="button"
                      onClick={onCampaignToggle}
                      disabled={campaignDisabled}
                      className={cn(
                        "flex w-full items-center gap-2 px-system-sm py-system-xs text-left hover:bg-basic-dark-blue/40 disabled:opacity-50",
                        oneToOne.enabled &&
                          campaignOn &&
                          "bg-basic-dark-blue/70",
                      )}
                    >
                      <Checkbox
                        checked={campaignOn}
                        disabled={campaignDisabled}
                        className={cn(someOn && "opacity-60")}
                        onChange={onCampaignToggle}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="truncate text-xs font-semibold uppercase tracking-wide text-gray-200">
                        {campaign.name}
                      </span>
                      <span className="ms-auto shrink-0 text-[10px] text-gray-400">
                        {oneToOne.enabled
                          ? "1 new ad set per ad"
                          : `${campaign.adSets.length} ad set${campaign.adSets.length === 1 ? "" : "s"}`}
                      </span>
                    </button>
                    {!oneToOne.enabled && (
                      <ul className="flex flex-col">
                        {campaign.adSets.map(adSet => {
                          const id = String(adSet.id)
                          const isSelected = selectedAdSets.has(id)
                          return (
                            <li key={id}>
                              <button
                                type="button"
                                onClick={() => toggleAdSet(adSet)}
                                className={cn(
                                  "flex w-full items-center gap-2 ps-system-lg pe-system-sm py-system-2xs text-left hover:bg-basic-dark-blue/40",
                                  isSelected && "bg-basic-dark-blue/70",
                                )}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => toggleAdSet(adSet)}
                                  onClick={e => e.stopPropagation()}
                                />
                                <span className="truncate text-sm text-white">
                                  {adSet.name}
                                </span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {needsDestinationUrl && (
        <div className="shrink-0 border-t border-basic-blue px-system-md py-system-sm">
          <SocialPostDestinationUrlSection
            value={destinationUrl}
            nonPvCount={nonPvTargetCount}
            oneToOne={oneToOne.enabled}
            postCount={postCount}
            showError={destinationUrlTouched}
            disabled={isBlasting}
            onChange={handleDestinationUrlChange}
            onBlur={handleDestinationUrlBlur}
          />
        </div>
      )}

      {/* 1:1 ad sets are created on Meta only — keep the control off the
       * TikTok tab so it can't read as applying to TikTok ad groups. */}
      {activePlatform === "meta" && !creativeTest.enabled && (
        <div className="shrink-0 border-t border-basic-blue px-system-md py-system-sm">
          <OneToOneAdSetSection
            value={oneToOne}
            showBudgetError={budgetTouched}
            disabled={isBlasting}
            onChange={handleOneToOneChange}
            onBudgetBlur={handleBudgetBlur}
          />
        </div>
      )}

      {/* Meta creative test (MD-5569) — Meta only and needs an EXISTING ad
       * set, so it is hidden on the TikTok tab and while 1:1 mode is on. */}
      {activePlatform === "meta" && !oneToOne.enabled && (
        <div className="shrink-0 border-t border-basic-blue px-system-md py-system-sm">
          <CreativeTestSection
            value={creativeTest}
            sourceCount={sources.length}
            metaSelectedCount={metaSelectedCount}
            adSetName={creativeTestAdSetName}
            eligibility={creativeTestEligibility}
            eligibilityLoading={creativeTestEligibilityLoading}
            eligibilityError={creativeTestEligibilityError}
            errors={creativeTestValidationErrors}
            showErrors={creativeTestTouched}
            disabled={isBlasting}
            onChange={handleCreativeTestChange}
          />
        </div>
      )}

      <div className="shrink-0 border-t border-basic-blue px-system-md py-system-sm">
        <ScheduleBlastControls
          onChange={handleScheduleChange}
          disabled={isBlasting}
        />
      </div>

      <div className="flex shrink-0 items-center justify-between gap-system-md border-t border-basic-blue px-system-md py-system-sm">
        <span className="text-xs text-gray-300">
          {targetCount} {targetNoun}
          {targetCount === 1 ? "" : "s"} selected
          {showTikTokTab &&
            totalSelectedCount > 0 &&
            ` (${metaSelectedCount} Meta · ${tiktokSelectedCount} TikTok)`}
        </span>
        <div className="flex items-center gap-system-sm">
          <button
            type="button"
            onClick={handleClose}
            disabled={isBlasting}
            className="text-sm text-gray-200 hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleLaunch}
            disabled={
              targetCount === 0 ||
              sources.length === 0 ||
              isBlasting ||
              hasUntaggedSources ||
              hasVideolessDraftsForTikTok ||
              hasSourcesWithoutTargets ||
              destinationUrlMissing ||
              budgetInvalid ||
              creativeTestInvalid ||
              (schedule.mode === "later" && !schedule.isValid)
            }
            title={launchBlockedReason}
            className="inline-flex items-center gap-system-2xs rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500 disabled:opacity-50"
          >
            {isBlasting && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {(() => {
              if (creativeTest.enabled) {
                const variants = `${sources.length} variant${sources.length === 1 ? "" : "s"}`
                if (schedule.mode === "later") {
                  return isBlasting
                    ? "Scheduling…"
                    : `Schedule creative test (${variants})`
                }
                return isBlasting
                  ? "Starting creative test…"
                  : `Create creative test (${variants})`
              }
              const targetLabel = `${targetCount} ${targetNoun}${targetCount === 1 ? "" : "s"}${oneToOne.enabled ? " · 1:1 ad sets" : ""}`
              if (schedule.mode === "later") {
                return isBlasting
                  ? "Scheduling…"
                  : `Schedule blast to ${targetLabel}`
              }
              return isBlasting ? "Starting blast…" : `Blast to ${targetLabel}`
            })()}
          </button>
        </div>
      </div>

      {nonTestAdSetNames && (
        <NonTestAdSetConfirmDialog
          adSetNames={nonTestAdSetNames}
          targetNoun={targetNoun}
          onCancel={() => setNonTestAdSetNames(null)}
          onConfirm={() => {
            setNonTestAdSetNames(null)
            void runBlast()
          }}
        />
      )}

      {/* Inline tag composer for an untagged draft. Rendered INSIDE the
       * Dialog so its fixed overlay shares the dialog's stacking context and
       * paints above it (the dialog root pins an extreme z-index). Applying
       * persists tags + the composed convention name onto the draft; the
       * cache invalidation refreshes the calling tab's rows, which flow back
       * in via `sources` and clear the warning. */}
      {tagTarget && (
        <AdNameBuilder
          isOpen
          onClose={() => setTagTarget(null)}
          initialSelection={
            tagTarget.kind === "post"
              ? postTags.get(tagTarget.postId)?.tags
              : (tagTarget.tags ?? undefined)
          }
          suggestTags={
            tagTarget.kind === "post"
              ? () => suggestPostTags(tagTarget)
              : undefined
          }
          onApply={(name, tags) => {
            if (tagTarget.kind === "post") {
              const postId = tagTarget.postId
              setPostTags(prev => new Map(prev).set(postId, { name, tags }))
              toast.success(
                "Tags set — every ad created from this post will carry them and the composed name",
              )
              return
            }
            const draftId = tagTarget.id
            const tagsAtSave = tagTarget.tags
            setSavingTags(prev => new Map(prev).set(draftId, tagsAtSave))
            void (async () => {
              try {
                await updateCreative({
                  id: draftId,
                  body: { name, tags },
                }).unwrap()
                toast.success(
                  "Tags saved — the draft now carries them (and the composed name) on every ad it launches",
                )
              } catch {
                setSavingTags(prev => {
                  const next = new Map(prev)
                  next.delete(draftId)
                  return next
                })
                toast.error(
                  "Couldn't save the tags. Try again, or open the draft in AdForge.",
                )
              }
            })()
          }}
        />
      )}
    </Dialog>
  )
}
